import Resource from "../../entities/resource";

/**
 * Resource actions
 *
 * Call order:
 *
 * RESOURCE_SAVE_QUEUE:	adds the item to the save queue
 * --> RESOURCE_SAVE:	modifies the state
 * --> RESOURCE_SAVE_SUCCESS: if all went well
 * --> RESOURCE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail RESOURCE_SAVE_QUEUE is during the call to RESOURCE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at RESOURCE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: RESOURCE_SAVE_SUCCESS, RESOURCE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or RESOURCE_PENDING_CHECK (it left the queue).
 */

export const RESOURCE_FETCH_ONE_QUEUE: any = 'RESOURCE_FETCH_ONE_QUEUE';
export const RESOURCE_FETCH_SUCCESS: any = 'RESOURCE_FETCH_SUCCESS';
export const RESOURCE_FETCH_FAILURE: any = 'RESOURCE_FETCH_FAILURE';
export const RESOURCE_FETCH_ONE_SUCCESS: any = 'RESOURCE_FETCH_ONE_SUCCESS';
export const RESOURCE_FETCH_ONE_FAILURE: any = 'RESOURCE_FETCH_ONE_FAILURE';

export const RESOURCE_SAVE: any = 'RESOURCE_SAVE';
export const RESOURCE_SAVE_QUEUE: any = 'RESOURCE_SAVE_QUEUE';
export const RESOURCE_SAVE_SUCCESS: any = 'RESOURCE_SAVE_SUCCESS';
export const RESOURCE_SAVE_FAILURE: any = 'RESOURCE_SAVE_FAILURE';

export const RESOURCE_SAVE_MANY: any = 'RESOURCE_SAVE_MANY';
export const RESOURCE_SAVE_MANY_QUEUE: any = 'RESOURCE_SAVE_MANY_QUEUE';
export const RESOURCE_SAVE_MANY_SUCCESS: any = 'RESOURCE_SAVE_MANY_SUCCESS';
export const RESOURCE_SAVE_MANY_FAILURE: any = 'RESOURCE_SAVE_MANY_FAILURE';

export const RESOURCE_REMOVE: any = 'RESOURCE_REMOVE';
export const RESOURCE_REMOVE_QUEUE: any = 'RESOURCE_REMOVE_QUEUE';
export const RESOURCE_REMOVE_SUCCESS: any = 'RESOURCE_REMOVE_SUCCESS';
export const RESOURCE_REMOVE_FAILURE: any = 'RESOURCE_REMOVE_FAILURE';

export const RESOURCE_RESTORE: any = 'RESOURCE_RESTORE';
export const RESOURCE_RESTORE_QUEUE: any = 'RESOURCE_RESTORE_QUEUE';
export const RESOURCE_RESTORE_SUCCESS: any = 'RESOURCE_RESTORE_SUCCESS';
export const RESOURCE_RESTORE_FAILURE: any = 'RESOURCE_RESTORE_FAILURE';

export const RESOURCE_RESTORE_MANY: any = 'RESOURCE_RESTORE_MANY';
export const RESOURCE_REMOVE_MANY: any = 'RESOURCE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const RESOURCE_SAVE_REVERT: any = 'RESOURCE_SAVE_REVERT';
export const RESOURCE_PENDING_CHECK: any = 'RESOURCE_PENDING_CHECK';

/* Fetch actions */
export const fetchResourceSuccess = (resources: Resource[], partial: boolean, lastFetch: number) => ({type: RESOURCE_FETCH_SUCCESS, resources, partial, lastFetch});
export const fetchResourceFailure = (err: any) => {
		console.error('ResourceAction: fetchResourceFailure', err);
		return {type: RESOURCE_FETCH_FAILURE, err};
};
export const fetchOneResourceQueue = (resourceId: string, sentAddedOn?: number) => ({type: RESOURCE_FETCH_ONE_QUEUE, resourceId, sentAddedOn});
export const fetchOneResourceSuccess = (resource: Resource | null, sentAddedOn?: number, resourceId: string = resource?.id) => ({type: RESOURCE_FETCH_ONE_SUCCESS, resourceId, resource, sentAddedOn});
export const fetchOneResourceFailure = (id: string, err: any) => {
		console.error('ResourceAction: fetchOneResourceFailure', err);
		return {type: RESOURCE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveResource = (resource: Resource) => {
    // console.debug('⮂ RESOURCE_SAVE action', resource?.id);
    return {type: RESOURCE_SAVE, resource};
}
export const saveResourceQueue = (resource: Resource, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ RESOURCE_SAVE_QUEUE action', resource?.id);
    return {type: RESOURCE_SAVE_QUEUE, resource, onQueued};
}
export const saveResourceSuccess = (resource: Resource, sentAddedOn?: number) => {
    // console.debug('⮂ RESOURCE_SAVE_SUCCESS action', resource?.id);
    return {type: RESOURCE_SAVE_SUCCESS, resource, sentAddedOn};
}
export const saveResourceFailure = (resource: Resource, status: number, message: string) => {
    console.warn(`⮂ RESOURCE_SAVE_FAILURE error saving resource: HTTP ${status}: ${message}`, resource?.id);
    return {type: RESOURCE_SAVE_FAILURE, resource, status, message};
};

/* SaveMany actions */
export const saveManyResources = (resources: Resource[]) => {
    // console.debug('⮂ RESOURCE_SAVE_MANY action', resources?.map(resource => resource?.id).join(','));
    return {type: RESOURCE_SAVE_MANY, resources};
}
export const saveManyResourcesQueue = (resources: Resource[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ RESOURCE_SAVE_MANY_QUEUE action', resources?.map(resource => resource.id).join(','));
    return {type: RESOURCE_SAVE_MANY_QUEUE, resources, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `resources` (OWA-1018) */
export const saveManyResourcesSuccess = (resources: Resource[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ RESOURCE_SAVE_MANY_SUCCESS action', resources?.map(resource => resource.id).join(','));
    return {type: RESOURCE_SAVE_MANY_SUCCESS, resources, sentAddedOns};
}
export const saveManyResourcesFailure = (resources: Resource[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_RESOURCES_FAILURE Error saving resources: HTTP ${status}: ${message}`, resources?.map(resource => resource.id).join(','));
		return {type: RESOURCE_SAVE_MANY_FAILURE, resources, status, message};
};

/* Remove actions */
export const removeResource = (resource: Resource) => ({ type: RESOURCE_REMOVE, resource });
export const removeResourceQueue = (resource: Resource) => ({ type: RESOURCE_REMOVE_QUEUE, resource });
export const removeResourceSuccess = (resource: Resource) => ({ type: RESOURCE_REMOVE_SUCCESS, resource });
export const removeResourceFailure = (resource: Resource, status: number, message: string) => {
	console.error(`Redux: removeResourceFailure Error removing resource: HTTP ${status}: ${message}`);
	return ({ type: RESOURCE_REMOVE_FAILURE, resource, status, message });
};

/* Restore actions */
export const restoreResource = (resource: Resource) => ({ type: RESOURCE_RESTORE, resource });
export const restoreResourceQueue = (resource: Resource) => ({ type: RESOURCE_RESTORE_QUEUE, resource });
export const restoreResourceSuccess = (resource: Resource, sentAddedOn?: number) => ({ type: RESOURCE_RESTORE_SUCCESS, resource, sentAddedOn });
export const restoreResourceFailure = (resource: Resource, status: number, message: string) => {
	console.error(`Redux: restoreResourceFailure Error restoring resource: HTTP ${status}: ${message}`);
	return ({ type: RESOURCE_RESTORE_FAILURE, resource, status, message });
};

/* RestoreMany actions */
export const restoreManyResources = (resources: Resource[]) => ({ type: RESOURCE_RESTORE_MANY, resources });

/* RemoveMany actions */
export const removeManyResources = (resources: Resource[]) => ({ type: RESOURCE_REMOVE_MANY, resources });

/* Pending save actions (OWA-1018) */
/**
 * The save `resource` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertResourceSave = (resource: Resource, options: {sentAddedOn?: number, current?: Resource | null, keep?: boolean} = {}) =>
    ({type: RESOURCE_SAVE_REVERT, resource, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkResourcePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: RESOURCE_PENDING_CHECK, live, refused, readAt});